import { Box, Card, CardContent, Stack, Typography, Button, IconButton } from "@mui/joy"
import GeekPlayer from "components/GeekPlayer";
import { useEffect, useRef, useState } from "react";
import { CandidateCardMeetButton, MeetingStatusChip } from "./components";
import { Delete, Download } from "@mui/icons-material";
import S3_URL from "utils/s3";
import MDEditor from '@uiw/react-md-editor';
import { convertUTCToLocalDisplay } from "utils/helpers";

interface CandidateCardProps {
  candidate: any;
  isActive: boolean;
  muted: boolean;
  setMuted: (muted: boolean) => void;
  playing: boolean;
  setPlaying: (playing: boolean) => void;
  onVisibilityChange: (isVisible: boolean) => void;
  onRemoveApplicant?: (id: string) => void;
}

const CandidateCard: React.FC<CandidateCardProps> = ({ 
  candidate,
  isActive,
  muted,
  setMuted,
  playing,
  setPlaying,
  onVisibilityChange,
  onRemoveApplicant,
}) =>  {
  const aspectRatio = "3/4";
  const cardRef = useRef<HTMLLIElement>(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisibilityChange(true);
          } else {
            onVisibilityChange(false);
          }
        });
      },
      {
        threshold: 0.7, // Card is considered visible when 70% is in view
        rootMargin: '0px 0px -20% 0px' // Consider card visible when it's in the top 80% of the viewport
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [onVisibilityChange]);

  const handleDownloadResume = async () => {
    if (!candidate.resumeDocumentId || !candidate.resume?.originalFileKey) {
      console.error('No resume available for download');
      return;
    }

    try {
      setIsDownloading(true);
      
      // Get file extension from originalFileKey
      const fileExtension = candidate.resume.originalFileKey.split('.').pop() || 'pdf';
      
      // Construct the S3 URL for the resume using the same S3_URL as GeekPlayer
      const s3Url = `${S3_URL}documents/${candidate.resume.originalFileKey}`;
      
      // Create a temporary link to download the file
      const response = await fetch(s3Url);
      if (!response.ok) {
        throw new Error('Failed to download resume');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${candidate.user.fullName} resume.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading resume:', error);
      // You might want to show a toast notification here
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card
      ref={cardRef}
      component="li"
      key={candidate.uuid}
      variant="soft"
      sx={{
        p: 0,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: { md: '200px' },
        overflow: 'hidden'
      }}
    >
      <CardContent 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          p: 0,
          backgroundColor: 'white'
        }}>

        {/* Video Section - Left side on desktop */}
        <Box
          sx={{
            width: { xs: '100%', md: '33.333%' },
            minWidth: { md: '200px' },
            position: 'relative',
            aspectRatio: { xs: aspectRatio, md: aspectRatio },
            flexShrink: 0,
            alignSelf: 'flex-start'
          }}
        >
          <GeekPlayer 
            key={candidate.videoUuid}
            videoId={candidate.videoUuid} 
            playing={isActive && playing}
            setGlobalPlaying={setPlaying}
            aspectRatio={aspectRatio}
            globalMuted={muted}
            setGlobalMuted={setMuted}
          />
        </Box>

        {/* Content Section - Right side on desktop */}
        <Stack direction="column" spacing={2} sx={{ flex: 1, p: 3 }}>
          <Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography level="h3" sx={{ mb: 1, fontWeight: 600 }}>
                  {candidate.user.fullName}
                </Typography>
                <MeetingStatusChip meetings={candidate.meetings || []} />
                {candidate.lastActiveAt && (
                  <Typography level="body-sm" sx={{ mt: 0.5, color: '#7B7F85' }}>
                    Active as of {convertUTCToLocalDisplay(candidate.lastActiveAt)}
                    {candidate.submittedToEmployer?.zipCode && ` to a job in ${candidate.submittedToEmployer.zipCode}`}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {onRemoveApplicant 
                  ? <Button 
                      variant="outlined"
                      size="sm"
                      onClick={() => onRemoveApplicant(candidate.uuid)}
                      sx={{ color: '#2e2d7e', borderColor: '#2e2d7e', '&:hover': { backgroundColor: '#F8F9FA' }}}
                      startDecorator={<Delete />}
                    >
                      Remove
                    </Button>
                  : <CandidateCardMeetButton candidate={candidate} />
                }
                {candidate.resumeDocumentId && candidate.resume?.originalFileKey && (
                  <Button
                    size="sm"
                    variant="plain"
                    startDecorator={<Download />}
                    onClick={handleDownloadResume}
                    loading={isDownloading}
                    sx={{
                      color: '#2e2d7e',
                      '&:hover': {
                        backgroundColor: '#F8F9FA'
                      }
                    }}
                  >
                    Download Resume
                  </Button>
                )}
              </Box>
            </Stack>
             
             {/* AI Generated Summary */}
             {candidate.summary && (
               <Box 
                 data-color-mode="light"
                 sx={{ 
                   mb: 2,
                   '& .wmde-markdown': {
                     backgroundColor: 'transparent',
                     fontFamily: 'inherit',
                     fontSize: '0.95rem',
                     '& h1, & h2, & h3, & h4, & h5, & h6': {
                       fontWeight: 600,
                       marginTop: '1em',
                       marginBottom: '0.5em',
                       color: '#1A1B1D',
                     },
                     '& h1': { fontSize: '1.5rem' },
                     '& h2': { fontSize: '1.25rem' },
                     '& h3': { fontSize: '1.1rem' },
                     '& p': {
                       marginBottom: '0.75em',
                       lineHeight: 1.6,
                       color: '#4A5568',
                     },
                     '& ul': {
                       marginBottom: '0.75em',
                       paddingLeft: '1.5em',
                       listStyleType: 'disc',
                       listStylePosition: 'outside',
                     },
                     '& ol': {
                       marginBottom: '0.75em',
                       paddingLeft: '1.5em',
                       listStyleType: 'decimal',
                       listStylePosition: 'outside',
                     },
                     '& li': {
                       marginBottom: '0.25em',
                       color: '#4A5568',
                       display: 'list-item',
                     },
                     '& strong': {
                       fontWeight: 600,
                       color: '#1A1B1D',
                     },
                     '& code': {
                       backgroundColor: '#F3F4F6',
                       padding: '0.125em 0.25em',
                       borderRadius: '3px',
                       fontSize: '0.9em',
                     },
                   },
                 }}
               >
                 <MDEditor.Markdown source={candidate.summary} />
               </Box>
             )}
          </Box>
          
        </Stack>
      </CardContent>
    </Card>
  );
}

export default CandidateCard;